import * as React from 'react';

import {MergeItemsModal} from 'shells/modal/merge-items-modal/merge-items-modal';
import {ThumbnailContent} from 'shells/thumbnail/thumbnail-content/thumbnail-content';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {useCopyContentToEdition} from '../graphql/hooks';

type Props = {
    edition: Edition;
    onClose: () => void;
};

export function TransferEditionContentModal(props: Props) {
    const {edition} = props;

    const {copyContentToEdition, isLoading: isCopyingContentToEdition} = useCopyContentToEdition();

    if (!edition.comparisonEdition) {
        // should never happen, but just in case
        return null;
    }

    const versionA = {
        id: edition.id,
        name: 'Version A',
        thumbnail: edition.emailTemplate && edition.emailTemplate.thumbnailUrl,
    } as const;

    const versionB = {
        id: edition.comparisonEdition.id,
        name: 'Version B',
        thumbnail:
            edition.comparisonEdition.emailTemplate &&
            edition.comparisonEdition.emailTemplate.thumbnailUrl,
    } as const;

    return (
        <MergeItemsModal
            title='Transfer content between versions'
            subtitle='Select a source version to transfer content and design. This allows you to transfer changes from one to another, without doing repetitive design work.'
            infoText='Transferring only impacts the content and design of your email. Subject line, preview text, and other sending details will remain the same.'
            mergeButtonText={(selectedEdition) =>
                selectedEdition
                    ? `Transfer content from ${selectedEdition.name}`
                    : 'Transfer content'
            }
            mergingText='Transferring…'
            isMerging={isCopyingContentToEdition}
            items={[versionA, versionB]}
            renderItem={(version) => (
                <ThumbnailContent
                    title={<span>{version.name}</span>} // passed as node to override bold style for strings
                    thumbnail={version.thumbnail}
                    thumbnailHeight={230}
                    // @ts-expect-error
                    backgroundColor={version.backgroundColor}
                />
            )}
            onMergeItems={(selectedVersion) => {
                const unselectedVersion = selectedVersion.id === versionA.id ? versionB : versionA;

                copyContentToEdition({
                    fromEditionId: selectedVersion.id,
                    toEditionId: unselectedVersion.id,
                }).then(() => {
                    props.onClose();
                });
            }}
            onClose={props.onClose}
            maxHeight={616}
        />
    );
}
